import { CampProps, SiteProps } from "../../../../../interfaces"
import { IFormData, ILineItemField } from "../../EditInvoice"
import React, { useEffect, useMemo, useRef, useState } from "react"
import useAdminForm from "../../../../../hooks/useAdminForm"
import { LINE_ITEM_KINDS, lineItemKind } from "~/constants/line_item_kinds"
import { formatMoney } from "~/utils/formatMoney"
import Form from "../../../../ui/Admin/Form"
import { ArrowPathRoundedSquareIcon } from "@heroicons/react/24/outline"
import classNames from "../../../../../utils/classNames"
import { TrashIcon } from "@heroicons/react/24/solid"
import IAddOn from "../../../../../interfaces/IAddOn"
import ProductDropdown from "../../../Products/ProductDropdown"
import { currencySymbol } from "~/utils/currencySymbol"
import InputFieldWithLabel from "~/components/Admin/Inputs/InputFieldWithLabel"
import sites from "~/api2/sites"
import InputFieldWithCurrency from "~/components/Admin/Inputs/InputFieldWithCurrency"
import { RESERVATION_KINDS } from "~/constants"

interface LineItemRowProps {
  camp: CampProps
  item: ILineItemField
  index: number
  isPreview: boolean
  site: SiteProps
  isRecurring: boolean
}

const MobileLineItemRow = ({ camp, item, index, isPreview, site, isRecurring }: LineItemRowProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const { resource, watch, setValue, isChanged } = useAdminForm<IFormData>()
  const items = watch("items")
  const totalPrice = watch(`items.${index}.total_amount`)
  const unitPrice = watch(`items.${index}.unit_amount`)
  const occurrences = watch(`items.${index}.occurrences`)
  const quantity = watch(`items.${index}.quantity`)
  const label = watch(`items.${index}.label`)

  const onChangeProduct = (product: IAddOn) => {
    setValue(`items.${index}.label`, product.label)
    setValue(`items.${index}.unit_amount`, product.amount_in_cents / 100)
    setValue(`items.${index}.add_on_id`, product.id)
    setValue(`items.${index}.quantity`, 1)
    setValue(`items.${index}.total_amount`, product.amount_in_cents / 100)
    setValue(`items.${index}.kind`, LINE_ITEM_KINDS.BASIC)
  }

  const onCreateProduct = (label: string) => {
    setValue(`items.${index}.label`, label)
    setValue(`items.${index}.kind`, LINE_ITEM_KINDS.CUSTOM)

    setValue(`items.${index}.add_on_id`, "")
  }

  useEffect(() => {
    const watchedItem = watch(`items.${index}`) as ILineItemField
    if (item?.reservation_id || watchedItem?.reservation_id) {
      return
    }

    setValue(`items.${index}.total_amount`, unitPrice * quantity)
  }, [unitPrice, quantity])

  useEffect(() => {
    if (item?.reservation_id) {
      const isReservationLineItem = !item.add_on_id && item.kind === "basic" && !item.parent_line_item_id
      const hasOccurrences = !!item.occurrences && item.occurrence_unit !== "months" && !isReservationLineItem
      let newTotal = item.total_amount / item.quantity

      if (hasOccurrences) newTotal = newTotal / item.occurrences

      setValue(`items.${index}.unit_amount`, newTotal)
    }
  }, [totalPrice])

  const onDeleteItem = () => {
    setValue(
      "items",
      items.filter((_, i) => i !== index),
    )
  }

  const quantityForMobile = resource.is_displaying_occurrences ? items[index].occurrences : items[index].quantity

  const isReservation = !!item.reservation_id?.length
  const isAlwaysApplied = !!item.is_always_applied
  const isFee = !!item.parent_line_item_id

  const labelComponent = useMemo(() => {
    if (isReservation || isAlwaysApplied) {
      return (
        <InputFieldWithLabel
          type="text"
          value={item?.label}
          onChange={(e) => setValue(`items.${index}.label`, e.target.value)}
          className="cursor-default focus:border-none focus:ring-0"
          autocomplete="off"
          disabled={isAlwaysApplied || !!item?.reservation_id || isFee}
        />
      )
    } else {
      return (
        <ProductDropdown
          site={site}
          disabledProductIds={items.map((li) => li.add_on_id)}
          onChange={onChangeProduct}
          createNewItem={onCreateProduct}
          selectedId={item.add_on_id}
          lineItem={item}
          dataTestId={`line-item-product-dropdown-${index}`}
        />
      )
    }
  }, [isReservation, isAlwaysApplied, item?.label, item.add_on_id, isFee, item?.reservation_id, sites, items])

  const isItemDeletable = () => {
    const unremovableKinds = [
      LINE_ITEM_KINDS.HOLIDAY_RATE,
      "weekend_rate",
      "additional_adults",
      "additional_kids",
      "additional_pets",
    ] as lineItemKind[]
    if (unremovableKinds.includes(item.kind)) return false
    return !item.reservation_id
  }

  const deleteButton = useMemo(() => {
    if (!isItemDeletable() || item.is_always_applied) {
      return null
    }

    return (
      <button
        onClick={() => onDeleteItem()}
        className="flex hover:bg-gray-100 rounded-md p-2 justify-center items-center"
      >
        <TrashIcon className="w-6 h-6 cursor-pointer" />
      </button>
    )
  }, [item.add_on_id, item.is_always_applied, item.reservation_id, onDeleteItem])

  if (item.is_hidden || (item.unit_amount === 0 && isPreview)) {
    return <></>
  }

  if (isPreview) {
    return (
      <div
        className={classNames(
          resource.is_displaying_occurrences
            ? "grid-cols-2 custom-500:grid-cols-10"
            : "grid-cols-2 custom-500:grid-cols-8",
          "grid mdlg:grid-cols-12 gap-2 mt-4 border-b border-gray-200 pb-4",
        )}
      >
        <div
          className={
            resource.is_displaying_occurrences
              ? "col-span-1 custom-500:col-span-2 mdlg:col-span-4"
              : "col-span-1 custom-500:col-span-2 mdlg:col-span-5"
          }
        >
          {label}
        </div>
        <div className="hidden custom-500:block col-span-2">{formatMoney(unitPrice, camp?.currency)}</div>
        {resource.is_displaying_occurrences && (
          <div className="hidden custom-500:block col-span-1">{occurrences || "-"}</div>
        )}
        <div className="hidden custom-500:block col-span-2">{quantity || "-"}</div>
        <div className="custom-500:flex flex-col col-span-1 custom-500:col-span-2">
          <div className="">{totalPrice ? formatMoney(totalPrice, camp?.currency) : "-"}</div>
          <div className="custom-500:hidden">
            {quantityForMobile > 1 ? `(${quantityForMobile} * ${items[index].unit_amount}$)` : <></>}
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <div className="mdlg:hidden rounded-lg shadow-sm border border-gray-30 p-3 mb-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-wrap items-center w-full">
            <div className="flex-grow mr-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
              {labelComponent}
            </div>

            {resource.is_displaying_occurrences && item.occurrences && (
              <div className="w-20">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nights</label>
                <InputFieldWithLabel type="number" value={item.occurrences} disabled />
              </div>
            )}
          </div>
          {deleteButton && <div className="ml-2 mt-6">{deleteButton}</div>}
        </div>

        <div className="flex items-start gap-2">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
            <InputFieldWithCurrency
              currency={camp.currency}
              hideCurrency
              disabled={!!item.reservation_id}
              value={unitPrice}
              onChange={(e) => setValue(`items.${index}.unit_amount`, e.target.value)}
            />
          </div>

          <div className="w-20">
            <label className="block text-sm font-medium text-gray-700 mb-1">Qty</label>
            <InputFieldWithLabel
              type="number"
              disableArrows
              value={quantity === 0 ? "" : quantity}
              disabled={!!item.reservation_id}
              min={0}
              onChange={(e) => {
                const val = e.target.value
                setValue(`items.${index}.quantity`, val === "" ? "" : Number(val))
              }}
            />
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Price</label>
            <div>
              <InputFieldWithCurrency
                currency={camp.currency}
                hideCurrency
                value={totalPrice}
                disabled={!item.reservation_id}
                onChange={(e) => setValue(`items.${index}.total_amount`, e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default MobileLineItemRow
